import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {useVersion} from 'react-admin';
import {RESOURCES as resources} from '../resources';
import {getGetHeaders} from '../utils/headers';
import {sortArrayByKey} from '../utils/dataFunctions';
import FieldDescription from './FieldDescription';
import FieldDivider from './FieldDivider';
import PalletLedgerTableRow from './PalletLedgerTableRow';
import {makeStyles} from '@material-ui/core/styles';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

const usePalletLedgerTableStyles = makeStyles((theme) => ({
    accordionDetail: {
        flexDirection: 'column'
    },
    accordionSummary: {
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
    },
    tbody: {
        maxHeight: 200,
        overflow: 'auto'
    },
    th: {
        lineHeight: '1.4',
        padding: '8px 12px 8px 12px',
        verticalAlign: 'bottom'
    },
}));

/**
 * A component for retrieving and displaying a Client's Pallet Ledger List
 *
 * @param record
 * @param props
 * @return {JSX.Element}
 * @constructor
 */
const PalletLedgerTable = ({record, ...props}) => {

    const [ledgerList, setLedgerList] = useState({
        entries: []
    });
    const classes = usePalletLedgerTableStyles();
    // Re-fetch when the view is refreshed, so a new adjustment appears here
    const version = useVersion();
    const recordId = (record && record.id) ? record.id : null;

    useEffect(
        () => {
            let cancelled = false;

            const fetchEntries = () => {
                const params = {
                    active: true,
                    id: recordId,
                    params: {
                        filter: {},
                        pagination: {page: 1, perPage: 10000},
                        sort: {field: "modified", order: "DESC"}
                    }
                };

                const cfg = resources.PalletLedger.GET_LIST(params);
                axios({
                    method: 'GET',
                    url: cfg.uri,
                    headers: getGetHeaders(),
                    maxBodyLength: Infinity,
                    maxContentLength: Infinity
                }).then((response) => {
                    if (cancelled) {
                        return;
                    }
                    const entries = (response.data && response.data.length > 0)
                        ? sortArrayByKey(response.data, 'modified', 'DESC')
                        : [];
                    setLedgerList(ledgerList => ({...ledgerList, entries: entries}));
                }).catch((error) => {
                    if (error.response) {
                        // Server response outside 2xx
                    } else if (error.request) {
                        // No response
                    }
                });
            };

            if (recordId) {
                fetchEntries();
            }

            return () => {
                cancelled = true;
            };

        }, [recordId, version]
    );

    const setRows = () => {
        return (
            ledgerList.entries.map((entry) => (
                <PalletLedgerTableRow
                    key={`pallet-ledger-row-${entry.id}`}
                    entry={entry}
                />
            ))
        )
    };

    return (
        <>
            <FieldDivider type="break" margin="bottom"/>
            <Accordion defaultExpanded>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon/>}
                    aria-controls="Pallet-Ledger-content"
                    id="Pallet-Ledger-header"
                    className={classes.accordionSummary}
                >
                    <FieldDescription header={true} margin="bottom" text="Pallet Count History"/>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetail}>
                    {ledgerList.entries.length === 0 &&
                    <FieldDescription
                        instructions={true} marginBottom={true}
                        text={`This Client has no pallet count adjustments.`}
                    />
                    }
                    <FieldDivider type="break" margin="bottom"/>

                    {ledgerList.entries.length > 0 &&
                    <div className="inventory-history-container">
                        <Table size="small" stickyHeader aria-label="Pallet Ledger Entries">
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.th}>Date<br/>Adjusted</TableCell>
                                    <TableCell align="right" className={classes.th}>Amount<br/>Adjusted</TableCell>
                                    <TableCell align="right" className={classes.th}>Updated<br/>Total</TableCell>
                                    <TableCell className={classes.th}>Reason</TableCell>
                                    <TableCell className={classes.th}>Notes</TableCell>
                                    <TableCell className={classes.th}>Adjusted by</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody className={classes.tbody}>
                                {[...setRows()]}
                            </TableBody>
                        </Table>
                    </div>
                    }
                </AccordionDetails>
            </Accordion>
            <FieldDivider type="break" margin="bottom"/>
        </>
    );
}

export default PalletLedgerTable;
